import * as AlertDialog from '@radix-ui/react-alert-dialog';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import {
  type AdminListItem,
  AdminList as AdminListSchema,
  type PendingInvite,
  PendingInviteList as PendingInviteListSchema,
} from '@uit/shared/schemas/admin';
import type { AdminRole } from '@uit/shared/schemas/auth';
import { useState } from 'react';
import { useApi } from '../../lib/api.js';
import { useAuth } from '../../lib/auth.js';
import s from './AdminsSection.module.scss';

// A stored email that fails this basic shape is flagged so an admin knows to
// remove the row; the list itself tolerates it rather than 400ing (T-093).
function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

function errorMessage(code: string | undefined, status: number): string {
  switch (code) {
    case 'last_admin_required':
      return 'There must be at least one admin — change another account to admin first.';
    case 'cannot_change_own_role':
      return 'You can’t change your own role.';
    case 'cannot_change_own_owner':
      return 'You can’t change your own owner access.';
    case 'cannot_delete_self':
      return 'You can’t remove your own account.';
    case 'forbidden':
      return 'Only an owner can grant or revoke owner access.';
    default:
      return `Action failed (${status}).`;
  }
}

export function AdminsSection() {
  const { admin: me } = useAuth();
  const { data, error, loading, refetch } = useApi<AdminListItem[]>('/api/admins', AdminListSchema);
  const { data: invites, refetch: refetchInvites } = useApi<PendingInvite[]>(
    '/api/admins/invites',
    PendingInviteListSchema,
  );
  const [busyId, setBusyId] = useState<string | null>(null);
  const [removeTarget, setRemoveTarget] = useState<AdminListItem | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [lastResetEmail, setLastResetEmail] = useState<string | null>(null);
  const [inviteBusyId, setInviteBusyId] = useState<string | null>(null);
  const [inviteFlash, setInviteFlash] = useState<string | null>(null);

  async function revokeInvite(id: string): Promise<void> {
    setInviteBusyId(id);
    setActionError(null);
    setInviteFlash(null);
    try {
      const res = await fetch(`/api/admins/invites/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!res.ok && res.status !== 204) setActionError(`Could not revoke invite (${res.status}).`);
    } finally {
      setInviteBusyId(null);
      void refetchInvites();
    }
  }

  async function resendInvite(id: string, email: string): Promise<void> {
    setInviteBusyId(id);
    setActionError(null);
    setInviteFlash(null);
    try {
      const res = await fetch(`/api/admins/invites/${id}/resend`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok && res.status !== 204) {
        setActionError(`Could not resend invite (${res.status}).`);
        return;
      }
      setInviteFlash(`Invite re-sent to ${email}.`);
    } finally {
      setInviteBusyId(null);
      void refetchInvites();
    }
  }

  async function changeRole(id: string, role: AdminRole): Promise<void> {
    setBusyId(id);
    setActionError(null);
    try {
      const res = await fetch(`/api/admins/${id}/role`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ role }),
      });
      if (!res.ok && res.status !== 204) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        setActionError(errorMessage(body.error, res.status));
      }
    } finally {
      setBusyId(null);
      void refetch();
    }
  }

  // Grant/revoke the owner capability (T-168). Owner-only endpoint; the menu
  // item that calls this is only rendered for an owner viewing the page.
  async function changeOwner(id: string, is_owner: boolean): Promise<void> {
    setBusyId(id);
    setActionError(null);
    try {
      const res = await fetch(`/api/admins/${id}/owner`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ is_owner }),
      });
      if (!res.ok && res.status !== 204) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        setActionError(errorMessage(body.error, res.status));
      }
    } finally {
      setBusyId(null);
      void refetch();
    }
  }

  async function remove(id: string): Promise<void> {
    setBusyId(id);
    setActionError(null);
    try {
      const res = await fetch(`/api/admins/${id}`, { method: 'DELETE', credentials: 'include' });
      if (!res.ok && res.status !== 204) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        setActionError(errorMessage(body.error, res.status));
      }
    } finally {
      setBusyId(null);
      setRemoveTarget(null);
      void refetch();
    }
  }

  async function resetPassword(id: string, email: string): Promise<void> {
    setBusyId(id);
    setActionError(null);
    try {
      const res = await fetch(`/api/admins/${id}/password-reset`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok && res.status !== 204) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        setActionError(errorMessage(body.error, res.status));
        return;
      }
      setActionError(null);
      setLastResetEmail(email);
    } finally {
      setBusyId(null);
    }
  }

  return (
    <section className={s.section}>
      <h2 className={s.sectionTitle}>Admins</h2>
      <p className={s.sectionSubtitle}>Manage who can access the portal and their access level.</p>

      {error && <div className={s.error}>Failed to load admins: {error.message}</div>}
      {actionError && (
        <div className={s.error} role="alert">
          {actionError}
        </div>
      )}
      {lastResetEmail && (
        <div role="status" className={s.muted}>
          Password reset email sent to {lastResetEmail}.
        </div>
      )}
      {!error && loading && !data && <div className={s.muted}>Loading…</div>}

      {data && (
        <table className={s.table}>
          <thead>
            <tr>
              <th scope="col">Name</th>
              <th scope="col">Email</th>
              <th scope="col">Role</th>
              <th scope="col" style={{ textAlign: 'right' }}>
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {data.map((a) => {
              const isSelf = a.id === me?.id;
              return (
                <tr key={a.id}>
                  <td>
                    {a.name}
                    {isSelf && <span className={s.youTag}> (you)</span>}
                    {a.is_owner && <span className={s.ownerTag}>Owner</span>}
                  </td>
                  <td>
                    {a.email}
                    {!isValidEmail(a.email) && <span className={s.invalidTag}> (invalid)</span>}
                  </td>
                  <td>
                    <select
                      className={s.select}
                      aria-label={`Role for ${a.email}`}
                      value={a.role}
                      disabled={isSelf || busyId === a.id}
                      onChange={(e) => void changeRole(a.id, e.target.value as AdminRole)}
                    >
                      <option value="onboarding_only">Onboarding only</option>
                      <option value="admin">Admin (full access)</option>
                    </select>
                  </td>
                  <td>
                    <div className={s.actions}>
                      {isSelf ? (
                        <span className={s.dash}>—</span>
                      ) : (
                        <DropdownMenu.Root>
                          <DropdownMenu.Trigger asChild>
                            <button
                              type="button"
                              className={s.kebab}
                              disabled={busyId === a.id}
                              aria-label={`Actions for ${a.email}`}
                            >
                              <KebabIcon />
                            </button>
                          </DropdownMenu.Trigger>
                          <DropdownMenu.Portal>
                            <DropdownMenu.Content className={s.menu} align="end" sideOffset={4}>
                              {me?.is_owner && (
                                <>
                                  <DropdownMenu.Item
                                    className={s.menuItem}
                                    onSelect={() => void changeOwner(a.id, !a.is_owner)}
                                  >
                                    {a.is_owner ? 'Remove owner access' : 'Make owner'}
                                  </DropdownMenu.Item>
                                  <DropdownMenu.Separator className={s.menuSep} />
                                </>
                              )}
                              <DropdownMenu.Item
                                className={s.menuItem}
                                onSelect={() => void resetPassword(a.id, a.email)}
                              >
                                Reset password
                              </DropdownMenu.Item>
                              <DropdownMenu.Separator className={s.menuSep} />
                              <DropdownMenu.Item
                                className={`${s.menuItem} ${s.menuItemDanger}`}
                                onSelect={() => setRemoveTarget(a)}
                              >
                                Remove
                              </DropdownMenu.Item>
                            </DropdownMenu.Content>
                          </DropdownMenu.Portal>
                        </DropdownMenu.Root>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {invites && invites.length > 0 && (
        <div className={s.invitesBlock}>
          <h3 className={s.invitesTitle}>Pending invites</h3>
          {inviteFlash && (
            <div role="status" className={s.muted}>
              {inviteFlash}
            </div>
          )}
          <table className={`${s.table} ${s.invitesTable}`}>
            <thead>
              <tr>
                <th scope="col">Name</th>
                <th scope="col">Email</th>
                <th scope="col">Role</th>
                <th scope="col">Expires</th>
                <th scope="col" style={{ textAlign: 'right' }}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {invites.map((inv) => (
                <tr key={inv.id}>
                  <td>{inv.name}</td>
                  <td>{inv.email}</td>
                  <td>{inv.role === 'admin' ? 'Admin (full access)' : 'Onboarding only'}</td>
                  <td>{new Date(inv.expires_at).toLocaleDateString()}</td>
                  <td>
                    <div className={s.actions}>
                      <button
                        type="button"
                        className={s.smallBtn}
                        disabled={inviteBusyId === inv.id}
                        onClick={() => void resendInvite(inv.id, inv.email)}
                      >
                        Resend
                      </button>
                      <button
                        type="button"
                        className={s.smallBtnDanger}
                        disabled={inviteBusyId === inv.id}
                        onClick={() => void revokeInvite(inv.id)}
                      >
                        Revoke
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <AlertDialog.Root
        open={removeTarget !== null}
        onOpenChange={(open) => {
          if (!open) setRemoveTarget(null);
        }}
      >
        <AlertDialog.Portal>
          <AlertDialog.Overlay className={s.overlay} />
          <AlertDialog.Content className={s.dialog}>
            <AlertDialog.Title className={s.dialogTitle}>Remove admin?</AlertDialog.Title>
            <AlertDialog.Description className={s.dialogBody}>
              {removeTarget && (
                <>
                  <strong>{removeTarget.name}</strong> ({removeTarget.email}) will lose portal
                  access. This can’t be undone.
                </>
              )}
            </AlertDialog.Description>
            <div className={s.dialogActions}>
              <AlertDialog.Cancel asChild>
                <button
                  type="button"
                  className={s.cancelBtn}
                  disabled={busyId === removeTarget?.id}
                >
                  Cancel
                </button>
              </AlertDialog.Cancel>
              <button
                type="button"
                className={s.dangerBtn}
                disabled={busyId === removeTarget?.id}
                onClick={() => {
                  if (removeTarget) void remove(removeTarget.id);
                }}
              >
                {busyId === removeTarget?.id ? 'Removing…' : 'Remove'}
              </button>
            </div>
          </AlertDialog.Content>
        </AlertDialog.Portal>
      </AlertDialog.Root>
    </section>
  );
}

function KebabIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
      <circle cx="12" cy="5" r="2" />
      <circle cx="12" cy="12" r="2" />
      <circle cx="12" cy="19" r="2" />
    </svg>
  );
}
